// Client-side mirror of server/src/config/pingolfScoring.js, used ONLY to show a live
// "here's roughly what you'd score" preview while entering a hole. The server always
// recomputes and persists the authoritative result on save - this never gets trusted
// as the real answer.
//
// The player self-reports which ball crossed the target (and whether the final total also
// doubled it), or, on a miss, the actual final score - see docs/PINGOLF.md.

const OVER_BONUS = 2;
const MAX_PEN = 7;

// What an unplayed hole scores on a card that is never finished. Mirrors
// UNPLAYED_HOLE_RESULT on the server, which is the value actually applied - this is only
// used to tell the player what leaving the card unfinished will cost them.
export const UNPLAYED_HOLE_RESULT = MAX_PEN;

const roundHalfUp = (value) => Math.floor(value + 0.5);
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// report is { reached: true, crossBall, doubled } or { reached: false, finalTotal }.
// Returns null if there isn't enough valid input yet to preview.
export function previewHoleResult(report, target, maxBalls) {
  if (!report || (report.reached !== true && report.reached !== false) || !target || !maxBalls) {
    return null;
  }

  if (report.reached) {
    const crossBall = parseInt(report.crossBall, 10);
    if (!Number.isInteger(crossBall) || crossBall < 1 || crossBall > maxBalls) {
      return null;
    }

    const doubled = Boolean(report.doubled);
    const holeResult = (crossBall - maxBalls) - (doubled ? OVER_BONUS : 0);
    return { reached: true, crossBall, doubled, holeResult };
  }

  const finalTotal = parseInt(report.finalTotal, 10);
  if (!Number.isInteger(finalTotal) || finalTotal < 0) {
    return null;
  }

  // Mirrors the server's rule: a score at or above the target means the hole was reached, so
  // it belongs in the ball picker. Checked here too, otherwise the modal previews a result the
  // server then rejects on save.
  if (finalTotal >= target) {
    return null;
  }

  const shortfall = (target - finalTotal) / target;
  const penalty = clamp(roundHalfUp(1 + (MAX_PEN - 1) * shortfall), 1, MAX_PEN);
  return { reached: false, finalTotal, doubled: false, holeResult: penalty };
}

export function formatVsPar(value) {
  if (value === null || value === undefined) return '-';
  return value > 0 ? `+${value}` : String(value);
}

// The week a board should be showing. Weeks are played in order, so the one that matters is
// the EARLIEST week still unfinalized - not the latest, which is week 8 of an 8-week season
// before anyone has played a single night. Falls back to the most recently finalized week
// once the season is over. Weeks with no host night attached are never candidates.
export function pickCurrentWeek(weeks) {
  const withSessions = (weeks || []).filter(week => (week.sessions || []).length > 0);
  if (withSessions.length === 0) return null;

  const unfinalized = withSessions
    .filter(week => week.status !== 'finalized')
    .sort((a, b) => a.week_number - b.week_number);
  if (unfinalized.length > 0) return unfinalized[0];

  return withSessions.sort((a, b) => b.week_number - a.week_number)[0];
}

// How to label that week. "In progress" only once its first host night has actually started;
// before that it is upcoming, which is the honest state for a season that hasn't begun.
export function describeWeekStage(week, now = Date.now()) {
  if (!week) return null;
  if (week.status === 'finalized') return 'final';

  const startTimes = (week.sessions || [])
    .map(session => new Date(session.event_date).getTime())
    .filter(time => !Number.isNaN(time));

  if (startTimes.length > 0 && Math.min(...startTimes) > now) return 'upcoming';
  return 'in progress';
}

// Which weeks a player's season total actually counted. The server keeps only the best
// (weekCount - dropCount) weekly scores, so a standings row showing every week will not add
// up to the total unless the dropped ones are marked. Mirrors computeSeasonTotal's rule:
// rank by points descending, keep the top N. Ties are broken by earlier week first, so the
// set kept is deterministic and matches nothing in particular on the server - only the sum
// matters there, and equal scores sum identically either way.
export function markCountedWeeks(weeklyPoints, weekCount, dropCount) {
  const counted = new Array(weeklyPoints.length).fill(false);
  const keep = Math.max((weekCount || weeklyPoints.length) - (dropCount || 0), 0);

  weeklyPoints
    .map((points, index) => ({ points, index }))
    .sort((a, b) => b.points - a.points || a.index - b.index)
    .slice(0, keep)
    .forEach(entry => { counted[entry.index] = true; });

  return counted;
}

// Mirror of computeNet in server/src/config/pingolfHandicap.js. Net SUBTRACTS the playing
// handicap from the card, golf convention - a player carrying a -5 average who shoots -3
// nets +2, and a player carrying a +5 average who shoots +3 nets -2. Lower is better.
// Anywhere the client needs a net it must come through here, not an inline expression, so
// the two sides can never drift apart again.
export function computeNet(cardResult, playingHandicap) {
  if (cardResult === null || cardResult === undefined) return null;
  if (!Number.isFinite(playingHandicap)) return null;
  return cardResult - playingHandicap;
}

// Mirror of computeWeeklyPayouts in server/src/config/pingolfSeason.js, used ONLY to preview
// a week pot's place-by-place split before finalization. The floor matters: a small pot does
// not split 50/30/20 at all, it hands each of the top three their buy-in back first, so a
// naive "50% of the pot" preview would promise money the payout never pays.
export function previewWeeklyPayouts({ pot, shares, floor, placesCount }) {
  const activeShares = (shares || []).slice(0, Math.min((shares || []).length, placesCount || 0));
  if (activeShares.length === 0 || !(pot > 0)) return [];

  const rawShares = activeShares.map(share => pot * share);
  if (rawShares.every(raw => raw >= floor)) {
    return rawShares.map(raw => Math.floor(raw));
  }

  const totalFloor = floor * activeShares.length;
  if (pot >= totalFloor) {
    const remainder = pot - totalFloor;
    const shareSum = activeShares.reduce((sum, share) => sum + share, 0);
    return activeShares.map(share => Math.floor(floor + (remainder * (share / shareSum))));
  }

  let remaining = pot;
  return activeShares.map(() => {
    const award = Math.max(Math.min(floor, remaining), 0);
    remaining -= award;
    return Math.floor(award);
  });
}

export function ordinalPlace(place) {
  const suffix = place === 1 ? 'st' : place === 2 ? 'nd' : place === 3 ? 'rd' : 'th';
  return `${place}${suffix}`;
}

// The stored index and playing handicap are a scoring AVERAGE versus par: negative means the
// player normally beats par. That is the right model value, but it is the wrong thing to put
// on a scorecard, because net subtracts it:
//
//     net = card - playingHandicap        <-  the model
//     net = card + displayHandicap        <-  what the player reads
//
// So every player-facing surface renders the negation: a player averaging 2 under par shows
// +2 (two strokes added - they are good enough to spot the field), and one averaging 8 over
// shows -8 (eight strokes off, the catch-up allowance). Displaying the raw average instead
// put "SCORE 0, HCP -2, NET +2" on screen, three numbers that cannot be made to add up.
//
// Everything player-facing must go through here so the scorecard always reads left to right.
export function toDisplayHandicap(playingHandicap) {
  // Guarded before the cast: Number(null) is 0 and Number('') is 0, so a player with no
  // handicap at all would otherwise render a confident "0" instead of a placeholder.
  if (playingHandicap === null || playingHandicap === undefined || playingHandicap === '') return null;
  const value = Number(playingHandicap);
  if (!Number.isFinite(value)) return null;
  // `-0` formats as "-0", so normalize it away.
  return value === 0 ? 0 : -value;
}

// The inverse of toDisplayHandicap, for the one direction that runs the other way: an admin
// typing a handicap in. The seed form used to take the raw vs-par average, so an admin typed
// -2 and every screen in the app then showed +2. Anything that accepts a handicap as input
// has to come back through here, or entry and display sit in opposite sign spaces.
export function fromDisplayHandicap(displayHandicap) {
  // Same guard as toDisplayHandicap: an empty field is "no handicap", not zero.
  if (displayHandicap === null || displayHandicap === undefined || displayHandicap === '') return null;
  const value = Number(displayHandicap);
  if (!Number.isFinite(value)) return null;
  // `-0` round-trips as "-0" through String(), so normalize it away.
  return value === 0 ? 0 : -value;
}

// Signed, player-facing handicap text. `decimals` is for the index, which carries one.
export function formatHandicap(playingHandicap, { decimals = 0, placeholder = '-' } = {}) {
  const value = toDisplayHandicap(playingHandicap);
  if (value === null) return placeholder;
  const magnitude = Math.abs(value).toFixed(decimals);
  if (value > 0) return `+${magnitude}`;
  if (value < 0) return `-${magnitude}`;
  return magnitude;
}

// Net is card MINUS playing handicap (see computeNet on the server), which the player reads
// as card PLUS the displayed handicap. Both spellings describe the same arithmetic.
export function describeNet(cardResult, playingHandicap) {
  const display = toDisplayHandicap(playingHandicap) || 0;
  const sign = display < 0 ? '-' : '+';
  return `${formatVsPar(cardResult)} ${sign} ${Math.abs(display)}`;
}

// The week an unattended board should be showing. pickCurrentWeek answers "which week is the
// league currently working on", which is the right answer for an admin screen and the wrong
// one for signage: the night after week 1 was played, the earliest unfinalized week is week 2,
// so the TV spent six days announcing that no cards had been submitted for a week nobody had
// played yet. This picks the most recent RELEVANT week instead - the one being played if there
// is one, otherwise the last one finished - so the panel is only empty when the season is.
//
// A week whose night has started but which the organizer has not finalized yet still counts as
// in progress, which is what keeps last night's board up until it is settled.
export function pickTvWeek(weeks, now = Date.now()) {
  const withSessions = (weeks || []).filter(week => (week.sessions || []).length > 0);
  if (withSessions.length === 0) return null;

  const byNumber = [...withSessions].sort((a, b) => a.week_number - b.week_number);

  const inProgress = byNumber.find(week => describeWeekStage(week, now) === 'in progress');
  if (inProgress) return inProgress;

  const finalized = byNumber.filter(week => week.status === 'finalized');
  if (finalized.length > 0) return finalized[finalized.length - 1];

  // Nothing played all season yet. Return the first scheduled week so the panel can name the
  // week it is waiting on rather than rendering no header at all.
  return byNumber[0];
}
